import { keepPreviousData } from '@tanstack/react-query';
import { Boxes, MessagesSquare, Search } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { JudgeKindIcon } from '@/components/atoms/JudgeKindIcon';
import { RobotLoader } from '@/components/atoms/RobotLoader';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useConversations } from '@/services/Conversations';
import { useJudges } from '@/services/Judges';
import { useModules } from '@/services/Modules';

import { NAV_COMMANDS } from './CommandPalette.constants';
import { useCommandPalette } from './CommandPalette.context';

type Item = {
	id: string;
	groupKey: 'GROUP_NAVIGATION' | 'GROUP_CONVERSATIONS' | 'GROUP_MODULES' | 'GROUP_JUDGES';
	to: string;
	primary: string;
	secondary?: string;
	hint?: string;
	icon: React.ReactNode;
};

const matches = (q: string, ...fields: string[]) => {
	if (!q.trim()) return true;
	const haystack = fields.join(' ').toLowerCase();

	return haystack.includes(q.trim().toLowerCase());
};

export const CommandPalette = () => {
	const { isOpen, close } = useCommandPalette();
	const { t } = useTranslation('translation', { keyPrefix: 'commandPalette' });
	const { t: tRoot } = useTranslation();
	const navigate = useNavigate();
	const [query, setQuery] = useState('');
	const [activeIdx, setActiveIdx] = useState(0);
	const inputRef = useRef<HTMLInputElement>(null);
	const listRef = useRef<HTMLDivElement>(null);

	const debouncedQuery = useDebouncedValue(query.trim());

	// Server-side search by threadId / userName — the client-side `matches`
	// filter below only sees the fetched page, so the query must reach the API.
	const { data: conversations, isFetching: isFetchingConversations } = useConversations(
		{ pageSize: 50, ...(debouncedQuery ? { search: debouncedQuery } : {}) },
		{ enabled: isOpen, placeholderData: keepPreviousData },
	);

	// Searching while the debounce window is open or the request is in flight.
	const isSearching = query.trim() !== debouncedQuery || isFetchingConversations;
	const { data: modules } = useModules({ enabled: isOpen });
	const { data: judgesData } = useJudges({}, { enabled: isOpen });

	useEffect(() => {
		if (isOpen) {
			setQuery('');
			setActiveIdx(0);
			setTimeout(() => inputRef.current?.focus(), 0);
		}
	}, [isOpen]);

	const items = useMemo<Item[]>(() => {
		const list: Item[] = [];

		NAV_COMMANDS.forEach((nav) => {
			const label = String(tRoot(nav.labelKey as never));

			if (matches(query, label, nav.to)) {
				list.push({
					id: nav.id,
					groupKey: 'GROUP_NAVIGATION',
					to: nav.to,
					primary: label,
					hint: nav.shortcut,
					icon: <nav.icon className="h-[14px] w-[14px]" />,
				});
			}
		});

		conversations?.items.forEach((c) => {
			if (matches(query, c.threadId, c.userName, c.companyName)) {
				list.push({
					id: `conv-${c.threadId}`,
					groupKey: 'GROUP_CONVERSATIONS',
					to: `/conversations/${c.threadId}`,
					primary: c.userName,
					secondary: `${c.companyName} · ${c.threadId}`,
					icon: <MessagesSquare className="h-[14px] w-[14px]" />,
				});
			}
		});

		modules?.forEach((m) => {
			if (matches(query, m.name, m.description)) {
				list.push({
					id: `mod-${m.name}`,
					groupKey: 'GROUP_MODULES',
					to: `/modules/${m.name}`,
					primary: m.name,
					secondary: m.description,
					icon: <Boxes className="h-[14px] w-[14px]" />,
				});
			}
		});

		judgesData?.items.forEach((j) => {
			if (matches(query, j.name, j.description)) {
				list.push({
					id: `judge-${j.id}`,
					groupKey: 'GROUP_JUDGES',
					to: `/judges/${j.id}`,
					primary: j.name,
					secondary: j.description,
					icon: <JudgeKindIcon kind={j.kind} />,
				});
			}
		});

		return list;
	}, [query, conversations, modules, judgesData, tRoot]);

	const grouped = useMemo(() => {
		const map = new Map<Item['groupKey'], Item[]>();

		items.forEach((it) => {
			const arr = map.get(it.groupKey) ?? [];

			arr.push(it);
			map.set(it.groupKey, arr);
		});
		return Array.from(map.entries());
	}, [items]);

	useEffect(() => {
		setActiveIdx(0);
	}, [query, items.length]);

	useEffect(() => {
		if (!isOpen) return;
		const el = listRef.current?.querySelector<HTMLElement>(`[data-cmd-idx="${activeIdx}"]`);

		el?.scrollIntoView({ block: 'nearest' });
	}, [activeIdx, isOpen]);

	const onKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'ArrowDown') {
			e.preventDefault();
			setActiveIdx((i) => Math.min(items.length - 1, i + 1));
		} else if (e.key === 'ArrowUp') {
			e.preventDefault();
			setActiveIdx((i) => Math.max(0, i - 1));
		} else if (e.key === 'Enter') {
			e.preventDefault();
			const it = items[activeIdx];

			if (it) {
				navigate(it.to);
				close();
			}
		}
	};

	if (!isOpen) return null;

	const palette = (
		<div className="mf-ai-agents">
			<div className="fixed inset-0 z-50 flex items-start justify-center bg-black/30 px-md pt-[12vh] backdrop-blur-sm">
				<button
					type="button"
					aria-label="Close command palette"
					className="absolute inset-0 z-0 cursor-default"
					onClick={close}
				/>
				<div
					className="relative z-10 w-full max-w-[640px] overflow-hidden rounded-lg border border-subtle bg-white shadow-2xl"
					role="dialog"
					aria-modal="true"
				>
					<div className="flex items-center gap-xs border-b border-subtle px-md py-sm">
						<Search className="h-sm w-sm text-subtle" />
						<input
							ref={inputRef}
							value={query}
							onChange={(e) => setQuery(e.target.value)}
							onKeyDown={onKeyDown}
							placeholder={t('PLACEHOLDER')}
							className="flex-1 bg-transparent text-[14px] leading-none text-default placeholder:text-subtle focus:outline-none"
							aria-label={t('PLACEHOLDER')}
						/>
					</div>

					<div ref={listRef} className="max-h-[420px] overflow-y-auto px-2xs py-2xs">
						{items.length > 0 &&
							grouped.map(([groupKey, groupItems]) => (
								<div key={groupKey} className="mb-xs last:mb-0">
									<p className="px-xs py-3xs text-[10px] font-medium uppercase tracking-wider text-subtle">
										{t(groupKey)}
									</p>
									<div className="flex flex-col gap-4xs">
										{groupItems.map((it) => {
											const globalIdx = items.indexOf(it);
											const isActive = globalIdx === activeIdx;

											return (
												<button
													key={it.id}
													type="button"
													data-cmd-idx={globalIdx}
													onMouseEnter={() => setActiveIdx(globalIdx)}
													onClick={() => {
														navigate(it.to);
														close();
													}}
													className={`flex w-full items-center gap-xs rounded-md px-xs py-2xs text-left text-[13px] transition-colors ${
														isActive ? 'bg-purple-50 text-purple-700' : 'text-default hover:bg-grey-25'
													}`}
												>
													<span
														className={`flex h-xl w-xl shrink-0 items-center justify-center rounded-md ${
															isActive ? 'text-purple-700' : 'text-subtle'
														}`}
													>
														{it.icon}
													</span>
													<span className="flex min-w-0 flex-1 flex-col">
														<span className="truncate font-medium leading-tight">{it.primary}</span>
														{it.secondary && (
															<span
																className={`truncate text-[11px] leading-tight ${
																	isActive ? 'text-purple-700/70' : 'text-subtle'
																}`}
															>
																{it.secondary}
															</span>
														)}
													</span>
													{it.hint && <kbd className="font-mono text-[10px] leading-none text-subtle">{it.hint}</kbd>}
												</button>
											);
										})}
									</div>
								</div>
							))}

						{isSearching && <RobotLoader label={t('SEARCHING')} />}

						{!isSearching && items.length === 0 && (
							<p className="px-md py-md text-[13px] text-subtle">{t('EMPTY', { q: query })}</p>
						)}
					</div>

					<div className="flex items-center justify-end gap-md border-t border-subtle bg-grey-25 px-md py-2xs text-[10px] text-subtle">
						<span>{t('HINT_NAVIGATE')}</span>
						<span>{t('HINT_OPEN')}</span>
						<span>{t('HINT_CLOSE')}</span>
					</div>
				</div>
			</div>
		</div>
	);

	return createPortal(palette, document.body);
};
